// Farbschema der Verwaltung anwenden (Kundenwunsch 12.10.2026; docs/redesign/umsetzung.md, Nachtrag 12.10.2026 Nr. 15).
// Gewählt wird es in $shell.theme ('hell' | 'dunkel', src/shell/shellState.js). Dunkel gilt nur, solange die Shell
// eingehängt ist; die Anmeldung bleibt immer hell. wendeFarbschemaAn() hält drei Dinge synchron:
//   - die Klasse html.farbschema-dunkel: Dunkelwerte der Tokens (src/css/redesign/tokens.css, nur @media screen); am
//     html-Element, weil DevExtreme-Overlays am body hängen,
//   - das Vuetify-Theme ($vuetify.theme.dark, Klassen theme--dark),
//   - die Browserfarbe (alle meta[name="theme-color"], Wert wie --chrome),
//   - die Hauptfarbe ($shell.hauptfarbe, Kundenwunsch 13.10.2026; Standard Petrol): Klasse html.hauptfarbe-petrol bzw.
//     html.hauptfarbe-orange (Blau sind die Grundwerte ohne Klasse; Tokens in tokens.css) und primary/accent/info des
//     Vuetify-Themes hell und dunkel. Auch sie gilt nur in der Shell.
// Aufrufer: MainLayout (watch auf $shell.theme mit immediate → wendeFarbschemaAn(true, this.$vuetify.theme), läuft vor
// dem ersten Rendern; beforeDestroy → wendeFarbschemaAn(false)) und der Router (Anmeldung „/“ → wendeFarbschemaAn(false)).
// Ohne Argument gilt der zuletzt gemeldete Zustand der Shell. Vor dem ersten Rendern setzt das Inline-Skript in
// public/index.html die Klasse.
// Den Theme-Dienst reicht MainLayout herein, statt dass diese Datei src/plugins/vuetify.js importiert: Der Import aus
// Router und MainLayout würde vuetify/lib (im Dev-Server samt dem CSS aller Vuetify-Komponenten) früher laden und damit
// die CSS-Reihenfolge verschieben. Vor dem ersten Einhängen der Shell war Vuetify nie dunkel.
// Drucken: Die Tokens fallen ohnehin auf hell zurück; das Vuetify-Theme ist für die Druckvorschau hell.
import { shell } from './shellState';

const KLASSE = 'farbschema-dunkel';
// Solange diese Klasse gesetzt ist, sind Übergänge aus (src/css/redesign/basis.css); sonst blendet beim Umschalten
// jede Fläche mit eigenem Übergang einzeln über.
const WECHSEL = 'farbschema-wechsel';
// Browserfarbe = --chrome hell bzw. dunkel (tokens.css); hell wie das Meta-Tag in public/index.html
const BROWSERFARBE = { hell: '#eef0f3', dunkel: '#0f1216' };
// Vuetify primary/accent/info je Hauptfarbe = --link hell bzw. dunkel (tokens.css)
const VUETIFY_FARBE = {
  blau: { light: '#2B7BB9', dark: '#85B9E0' },
  petrol: { light: '#3D5A80', dark: '#8FB0DC' },
  orange: { light: '#C24A00', dark: '#FF8A4C' }
};

let shellAktiv = false;
let vuetifyTheme = null;
let wechselNr = 0;

function setzeVuetify(dunkel) {
  if (vuetifyTheme && vuetifyTheme.dark !== dunkel) {
    vuetifyTheme.dark = dunkel;
  }
}

function setzeVuetifyFarbe(farbe) {
  if (!vuetifyTheme || !vuetifyTheme.themes) {
    return;
  }
  ['light', 'dark'].forEach(name => {
    const theme = vuetifyTheme.themes[name];
    const wert = VUETIFY_FARBE[farbe][name];
    if (theme && theme.primary !== wert) {
      theme.primary = wert;
      theme.accent = wert;
      theme.info = wert;
    }
  });
}

function setzeBrowserfarbe(dunkel) {
  const farbe = dunkel ? BROWSERFARBE.dunkel : BROWSERFARBE.hell;
  // auch das Meta-Tag, das das PWA-Plugin einfügt (es steht nach dem aus public/index.html)
  Array.prototype.forEach.call(document.querySelectorAll('meta[name="theme-color"]'), meta => {
    meta.setAttribute('content', farbe);
  });
}

// aktiv: true = Shell eingehängt, false = Shell abgebaut bzw. Anmeldung, weggelassen = unverändert;
// theme: Vuetify-Theme-Dienst ($vuetify.theme), einmal von MainLayout gemeldet
export function wendeFarbschemaAn(aktiv, theme) {
  if (typeof aktiv === 'boolean') {
    shellAktiv = aktiv;
  }
  if (theme) {
    vuetifyTheme = theme;
  }
  const dunkel = shellAktiv && shell.theme === 'dunkel';
  // Hauptfarbe nur wählbar für bconn_admin ($shell.hauptfarbeFrei), sonst Petrol (Kundenwunsch 13.10.2026)
  const gewaehlt = shell.hauptfarbeFrei ? shell.hauptfarbe : 'petrol';
  const farbe = shellAktiv && VUETIFY_FARBE[gewaehlt] ? gewaehlt : 'blau';
  const root = document.documentElement;
  const farbKlassen = Object.keys(VUETIFY_FARBE).filter(name => name !== 'blau').map(name => 'hauptfarbe-' + name);
  const farbeAlt = farbKlassen.filter(name => root.classList.contains(name)).join(' ');
  const farbeNeu = farbe === 'blau' ? '' : 'hauptfarbe-' + farbe;
  if (root.classList.contains(KLASSE) !== dunkel || farbeAlt !== farbeNeu) {
    const nr = ++wechselNr;
    root.classList.add(WECHSEL);
    if (dunkel) {
      root.classList.add(KLASSE);
    } else {
      root.classList.remove(KLASSE);
    }
    farbKlassen.forEach(name => {
      if (name === farbeNeu) {
        root.classList.add(name);
      } else {
        root.classList.remove(name);
      }
    });
    setzeBrowserfarbe(dunkel);
    // zwei Frames: Der erste wird mit den neuen Werten ohne Übergang gemalt; ein schneller zweiter Wechsel hält die
    // Übergänge weiter aus
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
      if (nr === wechselNr) {
        root.classList.remove(WECHSEL);
      }
    }));
  }
  setzeVuetify(dunkel);
  setzeVuetifyFarbe(farbe);
}

if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('beforeprint', () => setzeVuetify(false));
  window.addEventListener('afterprint', () => wendeFarbschemaAn());
}
